/**
 * Microservices API Layer
 * This implements proper microservices architecture with service discovery
 * Each service has its own endpoint but goes through proper routing
 *
 * Service URLs come from serviceConfig.ts (single source of truth).
 * To change a service URL/port, update ONLY the .env file.
 */

import { getServiceUrl } from '../../config/serviceConfig';

export interface ServiceConfig {
  name: string;
  baseUrl: string;
  port: number;
  endpoints: string[];
  status: 'active' | 'inactive' | 'maintenance';
}

// All service URLs resolved from serviceConfig (reads from .env)
const AUTH_SERVICE_URL = getServiceUrl('auth');
const IOT_SERVICE_URL = getServiceUrl('iot');
const BIOGAS_SERVICE_URL = getServiceUrl('biogas');
const COMMERCE_SERVICE_URL = getServiceUrl('commerce');
const MANUFACTURING_SERVICE_URL = getServiceUrl('manufacturing');
const GOVERNMENT_SERVICE_URL = getServiceUrl('government');
const GAUSHALA_SERVICE_URL = getServiceUrl('gaushala');
const PURIFICATION_SERVICE_URL = getServiceUrl('purification');


 /**
   * Service Registry - Defines all microservices in the SAUBHAGYA platform
   * URLs are loaded from environment variables (.env file)
   * Updated to match verified microservices (all 8 services)
   */
  export const SERVICE_REGISTRY: Record<string, ServiceConfig> = {
    'auth-service': {
      name: 'Authentication Service',
      baseUrl: AUTH_SERVICE_URL,
      port: 8081,
      endpoints: [
        '/api/auth/login',
        '/api/auth/register',
        '/api/auth/cattle/list',
        '/api/auth/cattle/store',
        '/oauth2/token',
        '/api/auth/users',
        '/api/auth/users/{id}',
        '/api/auth/users/{id}/toggle-status',
        '/api/auth/users/{id}/permissions',
        '/api/auth/profile',
        '/api/auth/logout'
      ],
      status: 'active'
    },
    'iot-service': {
      name: 'IoT Device Management Service',
      baseUrl: IOT_SERVICE_URL,
      port: 8080,
      endpoints: ['/api/v1/devices', '/api/v1/rfid-tags', '/api/v1/sensors'],
      status: 'active'
    },
    'biogas-service': {
      name: 'Biogas Contribution Service',
      baseUrl: BIOGAS_SERVICE_URL,
      port: 8082,
      endpoints: ['/api/v1/contributions', '/api/v1/contributions/farmer',
  '/api/v1/contributions/analytics'],
      status: 'active'
    },
    'transaction-service': {
      name: 'Biogas Transaction Service',
      baseUrl: BIOGAS_SERVICE_URL,
      port: 8082,
      endpoints: ['/api/v1/transactions', '/api/v1/biogas-production', '/api/v1/contributions'],
      status: 'active'
    },
    'commerce-service': {
      name: 'Commerce & Sales Service',
      baseUrl: COMMERCE_SERVICE_URL,
      port: 8083,
      endpoints: ['/api/commerce/sales', '/api/commerce/orders', '/api/commerce/contributions'],
      status: 'active'
    },
    'manufacturing-service': {
      name: 'Manufacturing Service',
      baseUrl: MANUFACTURING_SERVICE_URL,
      port: 8088,
      endpoints: ['/api/v1/production-batches', '/api/v1/manufacturing-units', '/api/v1/raw-materials'],
      status: 'active'
    },
    'government-service': {
      name: 'Government Dashboard Service',
      baseUrl: GOVERNMENT_SERVICE_URL,
      port: 8085,
      endpoints: ['/government/api/v1/dashboard', '/government/api/v1/schemes',
  '/government/api/v1/compliance'],
      status: 'active'
    },
    'gaushala-service': {
      name: 'Gaushala Management Service',
      baseUrl: GAUSHALA_SERVICE_URL,
      port: 8086,
      endpoints: [
        '/api/v1/gaushala/cattle',
        '/api/v1/gaushala/sheds',
        '/api/v1/gaushala/milk-records',
        '/api/v1/gaushala/health-records',
        '/api/v1/gaushala/food-history',
        '/api/v1/gaushala/inventory',
        '/api/v1/gaushala-access'
      ],
      status: 'active'
    },
    'purification-service': {
      name: 'Biogas Purification Service',
      baseUrl: PURIFICATION_SERVICE_URL,
      port: 8087,
      endpoints: [
        '/api/v1/cycles',
        '/api/v1/metrics/realtime',
        '/api/v1/quality/tests',
        '/api/v1/inventory',
        '/api/v1/slurry',
        '/api/v1/maintenance'
      ],
      status: 'active'
    }
  };

/**
 * Microservices Client - Handles service discovery and routing
 */
export class MicroservicesClient {
  private serviceRegistry = SERVICE_REGISTRY;

  /**
   * Get service configuration by name
   */
  getService(serviceName: string): ServiceConfig | null {
    return this.serviceRegistry[serviceName] || null;
  }

  /**
   * Handle 401 Unauthorized errors - create custom error with auth flag
   */
  private createAuthError(message: string): Error {
    const error = new Error(message);
    (error as any).isAuthError = true;
    return error;
  }

  /**
   * Make API call to specific microservice
   */
  async callService(serviceName: string, endpoint: string, options: RequestInit = {}): Promise<Response> {
    console.log('📡 [callService] API Call:', {
      service: serviceName,
      endpoint,
      method: options.method || 'GET',
      url: `${this.getService(serviceName)?.baseUrl}${endpoint}`
    });

    const service = this.getService(serviceName);

    if (!service) {
      throw new Error(`Service '${serviceName}' not found in registry`);
    }

    if (service.status !== 'active') {
      throw new Error(`Service '${serviceName}' is currently ${service.status}`);
    }

    const url = `${service.baseUrl}${endpoint}`;

    // Get token from localStorage
    const token = localStorage.getItem('saubhagya_jwt_token');
    console.log('📡 [callService] Token check:', {
      hasToken: !!token,
      tokenLength: token ? token.length : 0,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'NONE'
    });

    // IMPORTANT: Always include Authorization header if token exists
    // This is CRITICAL for authenticated endpoints
    const headers: Record<string, string> = {
      ...options.headers
    };

    // Only set Content-Type if not already provided and if body is not FormData
    // FormData requires browser to set Content-Type with proper boundary
    if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('📡 [callService] Authorization header set:', {
        header: `Bearer ${token.substring(0, 20)}...`
      });
    } else {
      console.warn('📡 [callService] WARNING: No token found in localStorage for authenticated endpoint!');
      console.warn('📡 [callService] This endpoint requires authentication but no token is available');
    }

    console.log('📡 [callService] Making fetch request to:', url);
    console.log('📡 [callService] Request headers:', {
      'Content-Type': headers['Content-Type'],
      'Authorization': headers['Authorization'] ? 'Bearer ***' : 'NOT SET'
    });

    const response = await fetch(url, {
      ...options,
      headers
    });

    console.log('📡 [callService] Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    // Handle 401 Unauthorized errors
    if (response.status === 401) {
      console.error('📡 [callService] Got 401 Unauthorized for endpoint:', endpoint);

      // Try to get error details from response
      let errorMessage = 'Access Denied - Your session may have expired';
      try {
        const errorData = await response.clone().json();
        console.error('📡 [callService] 401 Error Details:', errorData);
        console.error('📡 [callService] Request URL:', url);
        console.error('📡 [callService] Token exists:', !!token);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch (e) {
        console.error('📡 [callService] Could not parse error response');
      }

      // Throw auth error WITHOUT logging out the user
      // The calling service will handle the error appropriately
      const error = this.createAuthError(errorMessage);
      console.error('📡 [callService] Throwing auth error:', error.message);
      throw error;
    }

    return response;
  }

  /**
   * Get all active services
   */
  getActiveServices(): ServiceConfig[] {
    return Object.values(this.serviceRegistry).filter(service => service.status === 'active');
  }

  /**
   * Health check for all services
   */
  async healthCheck(): Promise<Record<string, boolean>> {
    const results: Record<string, boolean> = {};

    for (const [name, service] of Object.entries(this.serviceRegistry)) {
      try {
        const response = await fetch(`${service.baseUrl}/actuator/health`, {
          method: 'GET',
          timeout: 5000
        } as any);
        results[name] = response.ok;
      } catch {
        results[name] = false;
      }
    }

    return results;
  }

  /**
   * Get the base URL for a given service
   */
  private getServiceUrl(serviceName: string): string {
    const service = this.getService(serviceName);
    if (!service) {
      throw new Error(`Service '${serviceName}' not found in registry`);
    }
    return service.baseUrl;
  }

  /**
   * Make a GET request to any microservice
   * Used by services for fetching data from any backend service
   * @param serviceName - The service name from SERVICE_REGISTRY (e.g., 'auth-service', 'commerce-service')
   * @param endpoint - The API endpoint path (e.g., '/api/v1/users')
   * @param options - Optional query parameters
   */
  async get<T>(
    serviceName: string,
    endpoint: string,
    options?: { params?: Record<string, any> }
  ): Promise<{ data: T; status: number; success: boolean; error?: string }> {
    // Get the base URL for the specified service
    const baseUrl = this.getServiceUrl(serviceName);

    // Build URL with query params if provided
    let url = `${baseUrl}${endpoint}`;
    if (options?.params) {
      const queryString = new URLSearchParams(
        Object.entries(options.params).filter(([_, v]) => v !== undefined && v !== null)
      ).toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }

    console.log('📡 [microservicesClient.get] Fetching:', url, 'from service:', serviceName);

    const token = localStorage.getItem('saubhagya_jwt_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    console.log('📡 [microservicesClient.get] Response:', {
      status: response.status,
      ok: response.ok,
      service: serviceName,
    });

    if (response.status === 401) {
      throw this.createAuthError('Unauthorized - Access denied');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null as T,
        status: response.status,
        success: false,
        error: errorData.message || `HTTP error! status: ${response.status}`
      };
    }

    const data = await response.json();
    // Handle wrapped response format { success: true, data: ... }
    if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
      return { data: data.data as T, status: response.status, success: data.success };
    }
    return { data, status: response.status, success: true };
  }

  /**
   * Make a PUT request to any microservice
   * Used by services for updating data
   * @param serviceName - The service name from SERVICE_REGISTRY (e.g., 'auth-service', 'commerce-service')
   * @param endpoint - The API endpoint path
   * @param body - Optional request body
   */
  async put<T>(
    serviceName: string,
    endpoint: string,
    body?: any
  ): Promise<{ data: T; status: number; success: boolean; error?: string }> {
    const baseUrl = this.getServiceUrl(serviceName);
    const url = `${baseUrl}${endpoint}`;

    console.log('📡 [microservicesClient.put] Updating:', url, 'from service:', serviceName);

    const token = localStorage.getItem('saubhagya_jwt_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    console.log('📡 [microservicesClient.put] Response:', {
      status: response.status,
      ok: response.ok,
      service: serviceName,
    });

    if (response.status === 401) {
      throw this.createAuthError('Unauthorized - Access denied');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null as T,
        status: response.status,
        success: false,
        error: errorData.message || `HTTP error! status: ${response.status}`
      };
    }

    const data = await response.json();
    if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
      return { data: data.data as T, status: response.status, success: data.success };
    }
    return { data, status: response.status, success: true };
  }

  /**
   * Make a POST request to any microservice
   * Used by services for creating data
   * @param serviceName - The service name from SERVICE_REGISTRY (e.g., 'auth-service', 'commerce-service')
   * @param endpoint - The API endpoint path
   * @param body - Optional request body
   */
  async post<T>(
    serviceName: string,
    endpoint: string,
    body?: any
  ): Promise<{ data: T; status: number; success: boolean; error?: string }> {
    const baseUrl = this.getServiceUrl(serviceName);
    const url = `${baseUrl}${endpoint}`;

    console.log('📡 [microservicesClient.post] Creating:', url, 'from service:', serviceName);

    const token = localStorage.getItem('saubhagya_jwt_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    console.log('📡 [microservicesClient.post] Response:', {
      status: response.status,
      ok: response.ok,
      service: serviceName,
    });

    if (response.status === 401) {
      throw this.createAuthError('Unauthorized - Access denied');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null as T,
        status: response.status,
        success: false,
        error: errorData.message || `HTTP error! status: ${response.status}`
      };
    }

    const data = await response.json();
    if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
      return { data: data.data as T, status: response.status, success: data.success };
    }
    return { data, status: response.status, success: true };
  }

  /**
   * Make a PATCH request to any microservice
   * Used by services for partial updates
   * @param serviceName - The service name from SERVICE_REGISTRY (e.g., 'auth-service', 'commerce-service')
   * @param endpoint - The API endpoint path
   * @param body - Optional request body
   */
  async patch<T>(
    serviceName: string,
    endpoint: string,
    body?: any
  ): Promise<{ data: T; status: number; success: boolean; error?: string }> {
    const baseUrl = this.getServiceUrl(serviceName);
    const url = `${baseUrl}${endpoint}`;

    console.log('📡 [microservicesClient.patch] Updating:', url, 'from service:', serviceName);

    const token = localStorage.getItem('saubhagya_jwt_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    console.log('📡 [microservicesClient.patch] Response:', {
      status: response.status,
      ok: response.ok,
      service: serviceName,
    });

    if (response.status === 401) {
      throw this.createAuthError('Unauthorized - Access denied');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null as T,
        status: response.status,
        success: false,
        error: errorData.message || `HTTP error! status: ${response.status}`
      };
    }

    const data = await response.json();
    if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
      return { data: data.data as T, status: response.status, success: data.success };
    }
    return { data, status: response.status, success: true };
  }

  /**
   * Make a DELETE request to any microservice
   * Used by services for deleting data
   * @param serviceName - The service name from SERVICE_REGISTRY (e.g., 'auth-service', 'commerce-service')
   * @param endpoint - The API endpoint path
   */
  async delete<T>(
    serviceName: string,
    endpoint: string
  ): Promise<{ data: T; status: number; success: boolean; error?: string }> {
    const baseUrl = this.getServiceUrl(serviceName);
    const url = `${baseUrl}${endpoint}`;

    console.log('📡 [microservicesClient.delete] Deleting:', url, 'from service:', serviceName);

    const token = localStorage.getItem('saubhagya_jwt_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    console.log('📡 [microservicesClient.delete] Response:', {
      status: response.status,
      ok: response.ok,
      service: serviceName,
    });

    if (response.status === 401) {
      throw this.createAuthError('Unauthorized - Access denied');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null as T,
        status: response.status,
        success: false,
        error: errorData.message || `HTTP error! status: ${response.status}`
      };
    }

    // Handle empty response (204 No Content)
    if (response.status === 204) {
      return { data: null as T, status: response.status, success: true };
    }

    const data = await response.json().catch(() => null);
    if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
      return { data: data.data as T, status: response.status, success: data.success };
    }
    return { data, status: response.status, success: true };
  }
}

// Export singleton instance
export const microservicesClient = new MicroservicesClient();

/**
 * Service-specific clients for type safety
 */

export const IoTServiceClient = {
    async getCattleList() {
      // Call gaushala service with JWT authentication
      try {
        // Get JWT token from localStorage
        const token = localStorage.getItem('saubhagya_jwt_token');

        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const gaushalaUrl = GAUSHALA_SERVICE_URL;
        const response = await fetch(`${gaushalaUrl}/api/v1/gaushala/cattle`, {
          method: 'GET',
          headers
        });

        // Handle 401 Unauthorized
        if (response.status === 401) {
          console.error('🐄 [IoTServiceClient.getCattleList] Got 401 Unauthorized - Access Denied');
          // DON'T logout immediately
          // microservicesClient['handle401Error']();
          throw new Error('Unauthorized - Access denied');
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Transform PagedResponse to expected format
        if (data && data.content) {
          return {
            success: true,
            data: data.content
          };
        }

        return {
          success: true,
          data: data
        };
      } catch (error) {
        // Import toast dynamically to show error to user
        import('sonner').then(({ toast }) => {
          toast.error('Unable to fetch cattle data. Please check if the Gaushala service is running.');
        });

        // Return empty array when API fails - NO MOCK DATA
        return {
          success: false,
          data: []
        };
      }
    },

    async addCattle(cattleData: any) {
      const token = localStorage.getItem('saubhagya_jwt_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const gaushalaUrl = GAUSHALA_SERVICE_URL;
      const response = await fetch(`${gaushalaUrl}/api/v1/gaushala/cattle`, {
        method: 'POST',
        headers,
        body: JSON.stringify(cattleData)
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        console.error('🐄 [IoTServiceClient] Got 401 Unauthorized - Access Denied');
        // DON'T logout immediately
        // microservicesClient['handle401Error']();
        throw new Error('Unauthorized - Access denied');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { success: true, data: await response.json() };
    },

    async updateCattle(id: string, cattleData: any) {
      const token = localStorage.getItem('saubhagya_jwt_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const gaushalaUrl = GAUSHALA_SERVICE_URL;
      const response = await fetch(`${gaushalaUrl}/api/v1/gaushala/cattle/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(cattleData)
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        console.error('🐄 [IoTServiceClient] Got 401 Unauthorized - Access Denied');
        // DON'T logout immediately
        // microservicesClient['handle401Error']();
        throw new Error('Unauthorized - Access denied');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { success: true, data: await response.json() };
    },

     async deleteCattle(id: string) {
      const token = localStorage.getItem('saubhagya_jwt_token');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const gaushalaUrl = GAUSHALA_SERVICE_URL;
      const response = await fetch(`${gaushalaUrl}/api/v1/gaushala/cattle/${id}`, {
        method: 'DELETE',
        headers
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        console.error('🐄 [IoTServiceClient] Got 401 Unauthorized - Access Denied');
        // DON'T logout immediately
        // microservicesClient['handle401Error']();
        throw new Error('Unauthorized - Access denied');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { success: true };
    }
  };


 export const BiogasServiceClient = {
    async recordContribution(contributionData: any) {
      // Try biogas service first, fall back to mock success if service is unavailable
      try {
        const biogasUrl = BIOGAS_SERVICE_URL;
        const response = await fetch(`${biogasUrl}/api/v1/contributions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(contributionData)
        });

        const data = await response.json();

        if (response.ok) {
          return data;
        } else {
          // Return error response with validation details
          return {
            success: false,
            data: null,
            message: data.message || 'Validation error',
            error: data.error || 'Failed to create contribution'
          };
        }
      } catch (error) {
        // Return mock success response for demonstration
        return {
          success: true,
          id: `COW_DUNG_TXN_${Date.now()}`,
          message: 'Transaction recorded successfully (fallback mode)',
          ...contributionData
        };
      }
    },

    async getAllContributions(page: number = 0, size: number = 100) {
      try {
        // Fetch all contributions with field worker info
        const biogasUrl = BIOGAS_SERVICE_URL;
        const response = await fetch(`${biogasUrl}/api/v1/contributions`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          return data || [];
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('Failed to fetch biogas contributions:', error);
        // Import toast dynamically to show error to user
        import('sonner').then(({ toast }) => {
          toast.error('Unable to fetch biogas contributions. Please check if the Biogas service isrunning.');
        });
        return [];
      }
    },

    async getFarmerContributions(farmerId: string, page: number = 0, size: number = 20) {
      const response = await microservicesClient.callService('biogas-service',
  `/api/v1/contributions/farmer/${farmerId}?page=${page}&size=${size}`);
      return response.json();
    },

    async getContribution(contributionId: string) {
      const response = await microservicesClient.callService('biogas-service',
  `/api/v1/contributions/${contributionId}`);
      return response.json();
    },

    async processPayment(contributionId: string, paymentData: any) {
      const response = await microservicesClient.callService('biogas-service',
  `/api/v1/contributions/${contributionId}/payment`, {
        method: 'POST',
        body: JSON.stringify(paymentData)
      });
      return response.json();
    },

    async getContributionAnalytics(clusterId: string, fromDate: string, toDate: string) {
      const response = await microservicesClient.callService('biogas-service',
  `/api/v1/contributions/analytics?clusterId=${clusterId}&fromDate=${fromDate}&toDate=${toDate}`);
      return response.json();
    }
  };

  export default microservicesClient;
